// src/pages/Task/TaskForm.js
import React, { useState, useEffect, useRef } from "react";
// import Layout from "../../components/Layout";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTasks } from "../../context/TaskContext";
import { useProjects } from "../../context/ProjectContext";
import { useEmployees } from "../../context/EmployeeContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useUnsavedChanges } from "../../hooks/useUnsavedChanges";
import NavigationBlocker from "../../components/common/NavigationBlocker";

// 🟢 1. IMPORT CUSTOM FIELD COMPONENTS
import { useCustomFields } from "../../hooks/useCustomFields";
import CustomFieldRenderer from "../../components/common/CustomFieldRenderer";
import api from "../../api/axiosConfig";
import { toInputDate } from "../../utils/dateUtils";

export default function TaskForm() {
  const [isDirty, setIsDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [formOptions, setFormOptions] = useState({ projects: [], employees: [] });

  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  
  const { addTask, updateTask, fetchTaskFormOptions } = useTasks();
  const { projects: contextProjects } = useProjects();
  const { employees: contextEmployees } = useEmployees();

  const isEdit = Boolean(id);
  const loadedIdRef = useRef(null);

  // 🟢 1. FETCH PROJECTS & EMPLOYEES VIA SINGLE GET API ON MOUNT
  useEffect(() => {
    let isMounted = true;
    const loadDropdownData = async () => {
      setLoadingOptions(true);
      try {
        if (fetchTaskFormOptions) {
          const data = await fetchTaskFormOptions();
          if (isMounted && data) {
            setFormOptions({
              projects: data.projects || [],
              employees: data.employees || [],
            });
          }
        }
      } catch (err) {
        console.error("Failed to load dropdown options from GET API:", err);
      } finally {
        if (isMounted) setLoadingOptions(false);
      }
    };

    loadDropdownData();
    return () => { isMounted = false; };
  }, [fetchTaskFormOptions]);

  // Merge API data with context fallbacks
  const projects = formOptions.projects.length > 0 ? formOptions.projects : (contextProjects || []);
  const employees = formOptions.employees.length > 0 ? formOptions.employees : (contextEmployees || []);

  // 🟢 2. INITIALIZE CUSTOM FIELDS HOOK (uses 'tasks' module)
  const { customData, setCustomData, saveCustomFields } = useCustomFields('tasks', id || null);

  const getTodayInputDate = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  // 1. State with Costing & Measurement Fields
  const [taskData, setTaskData] = useState({
    title: "",
    description: "",
    projectId: "",
    projectName: "",
    assignedTo: "",
    employeeId: "",
    startDate: getTodayInputDate(),
    dueDate: getTodayInputDate(),
    status: "Pending",
    priority: "Medium",
    
    // Labor Estimation
    estimatedHours: "",
    
    // Work Measurement (BOQ)
    quantity: "",      // e.g., 100
    unit: "",          // e.g., Sq. Ft, Meters, Units
    unitRate: "",      // e.g., 50 (Price per unit)
    totalCost: "",     // e.g., 5000 (Auto-calculated)
    
    billable: true,
    notes: "",
  });

  // 2. Prefill Data via Dedicated GET /api/tasks/:id API (Edit Mode - Strict Single Execution Guard)
  useEffect(() => {
    if (!isEdit || !id) return;
    // Strict guard: NEVER fetch the same ID more than once
    if (loadedIdRef.current === String(id)) return;
    loadedIdRef.current = String(id);

    let isMounted = true;

    const loadTaskForEdit = async () => {
      try {
        const res = await api.get(`/tasks/${id}`);
        const fetched = res.data?.data || res.data?.task || res.data;

        if (isMounted && fetched) {
          setTaskData({
            title: fetched.title || "",
            description: fetched.description || "",
            projectId: fetched.projectId || fetched.project_id || "",
            projectName: fetched.projectName || fetched.project?.name || "",
            employeeId: fetched.employeeId || fetched.assigned_to || fetched.assignedEmployeeId || "",
            assignedTo: fetched.assignedTo || fetched.assignedName || "",
            startDate: toInputDate(fetched.startDate || fetched.start_date) || getTodayInputDate(),
            dueDate: toInputDate(fetched.dueDate || fetched.due_date) || getTodayInputDate(),
            status: fetched.status || "Pending",
            priority: fetched.priority || "Medium",
            estimatedHours: fetched.estimatedHours || "",
            quantity: fetched.quantity || "",
            unit: fetched.unit || "",
            unitRate: fetched.unitRate || "",
            totalCost: fetched.totalCost || fetched.cost || "",
            billable: fetched.billable !== undefined ? fetched.billable : true,
            notes: fetched.notes || "",
          });
        }
      } catch (err) {
        console.error("Single task fetch for edit error:", err);
        toast.error("Failed to load task details for editing");
      }
    };

    loadTaskForEdit();
    return () => { isMounted = false; };
  }, [id, isEdit]);

  // 3. Prefill Employee (From Profile Page if navigated with state)
  useEffect(() => {
    if (!isEdit && location.state?.prefillEmployeeId && employees.length > 0) {
       const empId = String(location.state.prefillEmployeeId);
       const selectedEmp = employees.find(e => String(e.id) === empId);
       
       if (selectedEmp) {
         setTaskData(prev => ({
           ...prev,
           employeeId: empId,
           assignedTo: selectedEmp.employeeName || selectedEmp.name
         }));
       }
    }
  }, [isEdit, location.state, employees]);

  // 4. Auto-Calculate Total Cost
  useEffect(() => {
    const qty = Number(taskData.quantity) || 0;
    const rate = Number(taskData.unitRate) || 0;
    const total = qty * rate;
    
    if (total !== Number(taskData.totalCost)) {
        setTaskData(prev => ({ ...prev, totalCost: total }));
    }
  }, [taskData.quantity, taskData.unitRate]);

  const handleChange = (e) => {
    setIsDirty(true);
    const { name, value, type, checked } = e.target;
    
    if (name === "employeeId") {
        const selectedEmp = employees.find(emp => String(emp.id) === String(value));
        setTaskData(prev => ({
            ...prev,
            employeeId: value,
            assignedTo: selectedEmp ? (selectedEmp.employeeName || selectedEmp.name) : ""
        }));
    }
    else if (name === "projectId") {
        const selectedProj = projects.find(p => String(p.id) === String(value));
        setTaskData(prev => ({
            ...prev,
            projectId: value,
            projectName: selectedProj ? selectedProj.name : ""
        }));
    } else {
        setTaskData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    }
  };

  // 🟢 5. SUBMIT TASK CREATION VIA POST API
  const handleSubmit = async (e) => { 
    e.preventDefault();

    if (!taskData.title.trim()) return toast.warn("Please add a Task Title.");

    setIsSubmitting(true);
    try {
      // Merge custom field data into payload
      const payload = {
          ...taskData,
          custom_data: customData
      };

      let result;

      if (isEdit) {
        result = await updateTask(id, payload); 
        if (result && saveCustomFields) await saveCustomFields(id);
      } else {
        result = await addTask(payload); // Sends POST /tasks API
        if (result && saveCustomFields) {
          const newId = typeof result === 'object' ? result.id : (typeof result === 'number' || typeof result === 'string' ? result : null);
          if (newId) await saveCustomFields(newId);
        }
      }

      if (result) {
          setIsDirty(false);
          setTimeout(() => navigate("/admin/Task"), 500);
      }
    } catch (err) {
      console.error("Task submit error:", err);
      toast.error("Failed to submit task. Please check server connection.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50 dark:bg-[#0d1117] p-4 sm:p-6 md:p-10 font-sans transition-colors">
        <div className="max-w-5xl mx-auto bg-white dark:bg-[#161b22] rounded-xl shadow-lg border border-gray-200 dark:border-[#30363d] overflow-hidden transition-colors">
            
            {/* Header */}
            <div className="bg-gray-50 dark:bg-[#161b22] px-6 sm:px-8 py-5 border-b border-gray-200 dark:border-[#30363d] flex justify-between items-center transition-colors">
                <div>
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                      {isEdit ? "Edit Task / Work Order" : "Create New Task"}
                  </h2>
                  <p className="text-xs text-gray-500 dark:text-[#848d97] mt-0.5">Fill in task details and assign workforce lead</p>
                </div>
                <div className="flex items-center gap-3">
                  {loadingOptions && (
                    <span className="text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/60 border border-blue-200 dark:border-blue-800 rounded-full px-3 py-1 animate-pulse font-medium">
                      Loading projects & team...
                    </span>
                  )}
                  <span className="text-sm text-gray-500 dark:text-[#848d97] italic hidden sm:inline">Fields marked with * are required</span>
                </div>
            </div>

            <form className="p-6 sm:p-8 space-y-8" onSubmit={handleSubmit}>
              
              {/* SECTION 1: CORE DETAILS */}
              <div>
                  <h3 className="text-sm font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-4 border-b border-gray-200 dark:border-[#30363d] pb-2">1. Task Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="md:col-span-2">
                          <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-1">Task Title <span className="text-red-500">*</span></label>
                          <input
                            type="text"
                            name="title"
                            value={taskData.title}
                            onChange={handleChange}
                            className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg px-4 py-3 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:ring-2 focus:ring-blue-500 outline-none transition"
                            placeholder="e.g., Pour Foundation for Block A"
                            required
                          />
                      </div>
                      <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-1">Project <span className="text-xs font-normal text-gray-400 dark:text-slate-500">(Optional)</span></label>
                          <select
                            name="projectId"
                            value={taskData.projectId}
                            onChange={handleChange}
                            disabled={loadingOptions}
                            className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg px-4 py-3 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none disabled:bg-gray-100 dark:disabled:bg-[#21262d] transition"
                          >
                            <option value="">{loadingOptions ? "Loading projects from API..." : "Select Project (Optional)"}</option>
                            {projects.map(p => (
                                <option key={p.id} value={p.id}>
                                  {p.name} {p.client_name ? `(${p.client_name})` : ''}
                                </option>
                            ))}
                          </select>
                      </div>
                      <div>
                          <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-1">Assigned Lead / Employee <span className="text-xs font-normal text-gray-400 dark:text-slate-500">(Optional)</span></label>
                          <select 
                              name="employeeId"
                              value={taskData.employeeId}
                              onChange={handleChange}
                              disabled={loadingOptions}
                              className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg px-4 py-3 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none disabled:bg-gray-100 dark:disabled:bg-[#21262d] transition"
                          >
                              <option value="">{loadingOptions ? "Loading team from API..." : "Select Employee (Optional)"}</option>
                              {employees.map(emp => (
                                  <option key={emp.id} value={emp.id}>
                                      {emp.employeeName || emp.name} ({emp.role || emp.trade || 'Staff'})
                                  </option>
                              ))}
                          </select>
                      </div>
                  </div>
              </div>

              {/* SECTION 2: PLANNING & SCHEDULE */}
              <div>
                <h3 className="text-sm font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-4 border-b border-gray-200 dark:border-[#30363d] pb-2">2. Schedule & Labor</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-1">Start Date</label>
                        <input
                          type="date"
                          name="startDate"
                          value={taskData.startDate}
                          onChange={handleChange}
                          className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg px-4 py-2.5 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-white dark:[color-scheme:dark] focus:ring-2 focus:ring-blue-500 outline-none transition"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-1">Due Date</label>
                        <input
                          type="date"
                          name="dueDate"
                          value={taskData.dueDate}
                          onChange={handleChange}
                          className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg px-4 py-2.5 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-white dark:[color-scheme:dark] focus:ring-2 focus:ring-blue-500 outline-none transition"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-1">Estimated Labor Hours</label>
                        <input
                          type="number"
                          name="estimatedHours"
                          value={taskData.estimatedHours}
                          onChange={handleChange}
                          className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg px-4 py-2.5 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:ring-2 focus:ring-blue-500 outline-none transition"
                          placeholder="e.g. 40"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-1">Status</label>
                        <select
                          name="status"
                          value={taskData.status}
                          onChange={handleChange}
                          className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg px-4 py-2.5 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none transition"
                        >
                          <option value="Pending">Pending</option>
                          <option value="In Progress">In Progress</option>
                          <option value="Completed">Completed</option>
                          <option value="On Hold">On Hold</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-1">Priority</label>
                        <select
                          name="priority"
                          value={taskData.priority}
                          onChange={handleChange}
                          className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg px-4 py-2.5 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none transition"
                        >
                          <option value="High">High</option>
                          <option value="Medium">Medium</option>
                          <option value="Low">Low</option>
                        </select>
                    </div>
                    <div className="flex items-end pb-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              name="billable"
                              checked={taskData.billable}
                              onChange={handleChange}
                              className="w-5 h-5 text-blue-600 rounded dark:bg-[#0d1117] dark:border-[#30363d] focus:ring-blue-500"
                            />
                            <span className="text-sm font-medium text-gray-700 dark:text-slate-300">Billable to Client</span>
                        </label>
                    </div>
                </div>
              </div>

              {/* Description */}
              <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-1">Additional Notes / Description</label>
                  <textarea
                    name="description"
                    rows="3"
                    value={taskData.description}
                    onChange={handleChange}
                    className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg px-4 py-3 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:ring-2 focus:ring-blue-500 outline-none transition"
                    placeholder="Describe the scope of work..."
                  ></textarea>
              </div>

              {/* SECTION 3: WORK MEASUREMENT & COSTING (AT VERY BOTTOM) */}
              <div className="bg-blue-50/70 dark:bg-[#1c2128] p-6 rounded-xl border border-blue-100 dark:border-[#30363d] transition-colors">
                  <h3 className="text-sm font-bold text-blue-800 dark:text-blue-400 uppercase tracking-wide mb-4 flex items-center gap-2">
                    3. Measurement & Valuation
                    <span className="text-[10px] bg-blue-200 dark:bg-blue-950/80 text-blue-800 dark:text-blue-300 border border-blue-300 dark:border-blue-800/60 px-2.5 py-0.5 rounded-full font-semibold">Real-time Calculation</span>
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                      <div>
                          <label className="block text-xs font-bold text-gray-600 dark:text-slate-300 uppercase mb-1.5">Quantity</label>
                          <input
                            type="number"
                            name="quantity"
                            value={taskData.quantity}
                            onChange={handleChange}
                            className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg px-3 py-2.5 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:ring-2 focus:ring-blue-500 outline-none transition"
                            placeholder="e.g. 100"
                          />
                      </div>
                      <div>
                          <label className="block text-xs font-bold text-gray-600 dark:text-slate-300 uppercase mb-1.5">Unit (UOM)</label>
                          <select 
                             name="unit" 
                             value={taskData.unit} 
                             onChange={handleChange}
                             className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg px-3 py-2.5 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none transition"
                          >
                             <option value="">Select Unit</option>
                             <option value="Sq. Ft">Sq. Ft</option>
                             <option value="Sq. Meter">Sq. Meter</option>
                             <option value="Cubic Meter">Cubic Meter</option>
                             <option value="Running Meter">Running Meter</option>
                             <option value="Hours">Hours</option>
                             <option value="Nos">Nos (Units)</option>
                             <option value="Lump Sum">Lump Sum</option>
                          </select>
                      </div>
                      <div>
                          <label className="block text-xs font-bold text-gray-600 dark:text-slate-300 uppercase mb-1.5">Unit Rate (₹)</label>
                          <input
                            type="number"
                            name="unitRate"
                            value={taskData.unitRate}
                            onChange={handleChange}
                            className="w-full border border-gray-300 dark:border-[#30363d] rounded-lg px-3 py-2.5 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:ring-2 focus:ring-blue-500 outline-none transition"
                            placeholder="e.g. 500"
                          />
                      </div>
                      <div>
                          <label className="block text-xs font-bold text-gray-600 dark:text-slate-300 uppercase mb-1.5">Total Estimated Cost</label>
                          <input
                            type="number"
                            name="totalCost"
                            value={taskData.totalCost}
                            readOnly
                            className="w-full border border-gray-300 dark:border-[#30363d] bg-gray-100 dark:bg-[#0d1117]/80 text-gray-900 dark:text-emerald-400 font-bold rounded-lg px-3 py-2.5 cursor-not-allowed transition"
                            placeholder="0"
                          />
                      </div>
                  </div>
              </div>

              {/* 🟢 4. RENDER CUSTOM FIELDS HERE */}
              <CustomFieldRenderer 
                  module="tasks" 
                  customData={customData} 
                  onChange={setCustomData} 
              />

              {/* Buttons */}
              <div className="pt-6 border-t border-gray-200 dark:border-[#30363d] flex justify-end gap-3 transition-colors">
                <button 
                    type="button"
                    onClick={() => navigate("/admin/Task")}
                    className="px-6 py-2.5 rounded-lg border border-gray-300 dark:border-[#30363d] text-gray-700 dark:text-slate-300 font-medium hover:bg-gray-50 dark:hover:bg-[#21262d] transition"
                >
                    Cancel
                </button>
                <button 
                    type="submit" 
                    disabled={isSubmitting || loadingOptions}
                    className="px-8 py-2.5 rounded-lg bg-gray-900 dark:bg-[#E8612C] text-white font-medium hover:bg-gray-800 dark:hover:bg-[#D44E1C] shadow-lg transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                      <span>{isEdit ? "Updating Work Order..." : "Creating Work Order..."}</span>
                    </>
                  ) : (
                    isEdit ? "Update Work Order" : "Create Work Order"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {useUnsavedChanges(isDirty)}
      <NavigationBlocker isDirty={isDirty} />
    </>
  );
}